/* eslint-disable no-console */
import * as Sentry from '@sentry/react';
import { TOKEN } from 'constants/index';
import type { History } from 'history';
import type { GraphQLFormattedError } from 'graphql';

import {
    ARCHIVED_CODE,
    AUTHENTICATION_REQUIRED_CODE,
    AUTHENTICATION_REQUIRED_MESSAGE,
    BANNED_CODE,
    EXPIRED_TOKEN_CODE,
} from './auth-errors';

interface ErrorHandlerArgs {
    graphQLErrors?: ReadonlyArray<GraphQLFormattedError>;
}

export const createErrorHandler =
    (history: History) =>
    ({ graphQLErrors }: ErrorHandlerArgs): void => {
        if (graphQLErrors && graphQLErrors.length > 0) {
            const [error] = graphQLErrors;
            const { message, locations, path } = error as any;

            const formattedMessage = [
                '[GraphQLError]',
                `Message: ${message}`,
                `Location: ${JSON.stringify(locations)}`,
                `Path: ${path}`,
            ].join('\n');

            const code = error.extensions?.code;

            // An expired access token is expected and self-healing: the token refresh link renews
            // the token and replays the request, and reports it once if the refresh itself fails.
            // Reporting every occurrence buried real GraphQL errors under thousands of duplicate
            // events (Sentry TOTAPP-3DM). A breadcrumb keeps the context on the errors that follow.
            // Mirrors SILENT_ERROR_CODES in systems/api/src/middlewares/sentry.ts.
            if (code === EXPIRED_TOKEN_CODE) {
                Sentry.addBreadcrumb({ category: 'graphql', level: 'info', message: formattedMessage });
                // Pas un `console.error` : rien n'est cassé pour l'utilisateur — le lien de refresh renouvelle
                // le token et rejoue la requête. En rouge, ces messages (une ligne par requête en vol au moment
                // de l'expiration) noyaient les vraies erreurs. `console.debug` reste lisible en niveau
                // « Verbose » pour déboguer une boucle de refresh.
                console.debug(formattedMessage);
            } else {
                // TODO: Update with the new logger when available
                Sentry.captureEvent({
                    level: 'error',
                    message: formattedMessage,
                    contexts: { graphqlError: error as any },
                });
                console.error(formattedMessage);
            }

            if (code === BANNED_CODE || code === ARCHIVED_CODE) {
                window.TOTEM.storage.remove(TOKEN);
                history.replace(`/sven?reason=${code}`);
                // TODO should not be required, but app does not rerender on location change
                history.go(0);
            }
            if (code === AUTHENTICATION_REQUIRED_CODE || error.message === AUTHENTICATION_REQUIRED_MESSAGE) {
                window.TOTEM.storage.remove(TOKEN);
                history.replace('/login');
                history.go(0);
            }
        }
    };
